import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { api } from '@/api/client';
import { Icon } from './Icon';
import s from './CarrierCompanyInput.module.css';

type Carrier = { id: string; name: string; code?: string | null; type: 'EXPRESS' | 'LOGISTICS'; isActive: boolean };
type CarrierPage = { items: Carrier[]; pagination: { hasNext: boolean } };

/** Shipping reads the shared active catalogue, without requiring settings access.
 * The selected name remains the V1/V2 shipment snapshot; custom and historical names stay valid.
 */
export function CarrierCompanyInput({ value, onChange, disabled = false, autoFocus = false }: {
  value: string; onChange: (value: string) => void; disabled?: boolean; autoFocus?: boolean;
}) {
  const id = useId();
  const root = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const [carriers, setCarriers] = useState<Carrier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [active, setActive] = useState(-1);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 240, maxHeight: 240 });

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError('');
    void (async () => {
      const rows: Carrier[] = [];
      let page = 1;
      do {
        const result = await api.get<CarrierPage>('/api/carrier-companies', { page, pageSize: 500 }, controller.signal);
        rows.push(...result.items.filter((carrier) => carrier.isActive));
        if (!result.pagination.hasNext) break;
        page += 1;
      } while (!controller.signal.aborted);
      if (!controller.signal.aborted) setCarriers([...new Map(rows.map((carrier) => [carrier.name, carrier])).values()]);
    })().catch((cause: Error) => { if (!controller.signal.aborted) setError(cause.message || '读取失败'); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [retry]);

  const query = search.trim().toLocaleLowerCase();
  const options = carriers.filter((carrier) => !query || `${carrier.name} ${carrier.code || ''}`.toLocaleLowerCase().includes(query));
  const choose = (name: string) => { input.current?.focus(); onChange(name); setOpen(false); setActive(-1); };
  const show = () => { if (!disabled) { setSearch(''); setActive(-1); setOpen(true); } };

  useEffect(() => {
    if (!open || disabled) return;
    const place = () => {
      const rect = input.current?.getBoundingClientRect();
      if (!rect) return;
      const below = window.innerHeight - rect.bottom - 12;
      const above = rect.top - 12;
      const up = below < 180 && above > below;
      const maxHeight = Math.min(260, Math.max(80, up ? above : below));
      const width = Math.min(Math.max(rect.width, 220), window.innerWidth - 24);
      setPosition({ top: up ? rect.top - maxHeight - 4 : rect.bottom + 4, left: Math.max(12, Math.min(rect.left, window.innerWidth - width - 12)), width, maxHeight });
    };
    const outside = (event: PointerEvent) => { if (!root.current?.contains(event.target as Node) && !list.current?.contains(event.target as Node)) setOpen(false); };
    place(); window.addEventListener('resize', place); window.addEventListener('scroll', place, true);
    document.addEventListener('pointerdown', outside);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); document.removeEventListener('pointerdown', outside); };
  }, [open, disabled]);

  useEffect(() => { if (active >= 0) document.getElementById(`${id}-${active}`)?.scrollIntoView({ block: 'nearest' }); }, [active, id]);

  return <div className={s.root} ref={root}>
    <input ref={input} className={s.input} role="combobox" aria-label="物流公司" aria-autocomplete="list" aria-expanded={open && !disabled}
      aria-controls={id} aria-activedescendant={open && active >= 0 ? `${id}-${active}` : undefined}
      value={value} required maxLength={100} autoFocus={autoFocus} disabled={disabled} autoComplete="off" placeholder="选择或搜索物流/快递公司"
      onFocus={show} onClick={() => { if (!open) show(); }} onChange={(event) => { onChange(event.target.value); setSearch(event.target.value); setActive(-1); setOpen(true); }}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && open) { event.preventDefault(); event.stopPropagation(); setOpen(false); }
        else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); if (!open) show(); setActive((n) => options.length ? (n + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length : -1); }
        else if (event.key === 'Enter' && open) { event.preventDefault(); if (active >= 0 && options[active]) choose(options[active].name); else setOpen(false); }
        else if (event.key === 'Tab') setOpen(false);
      }} onBlur={(event) => { if (!list.current?.contains(event.relatedTarget) && !root.current?.contains(event.relatedTarget)) setOpen(false); }} />
    <button type="button" className={s.toggle} disabled={disabled} aria-label="选择已录入物流公司" onMouseDown={(event) => event.preventDefault()} onClick={() => { if (open) setOpen(false); else { input.current?.focus(); show(); } }}><Icon name="chev" size={14} /></button>
    {open && !disabled && createPortal(<div className={s.dropdown} style={position} ref={list} onMouseDown={(event) => event.preventDefault()}>
      {loading && <p role="status">正在读取物流公司…</p>}
      {error && <div className={s.error} role="alert">公司列表读取失败：{error}<button type="button" onClick={() => setRetry((n) => n + 1)}>重试</button></div>}
      <div role="listbox" id={id} aria-label="已录入物流/快递公司">{options.map((carrier, index) => <button key={carrier.name} id={`${id}-${index}`} type="button" role="option" aria-selected={value === carrier.name} className={index === active ? s.active : undefined} onClick={() => choose(carrier.name)}>
        <span>{carrier.name}</span><small>{carrier.type === 'EXPRESS' ? '快递' : '物流'}{carrier.code ? ` · ${carrier.code}` : ''}</small>
      </button>)}</div>
      {!loading && !error && !options.length && <p>{query ? '没有匹配的公司，可继续使用输入的名称' : '暂无启用的公司，可手工输入名称'}</p>}
    </div>, document.body)}
  </div>;
}
